import { useState, useEffect, useCallback, useRef } from 'react'
import { ServiceConfig, ServiceType } from '../types'
import { apiService } from '../services/api'
import { getAllServiceConfigs } from '../config/services'

interface ServiceMonitoringState {
  services: ServiceConfig[]
  isLoading: boolean
  apiConnected: boolean
  lastUpdate: Date | null
  error: string | null
}

interface ServiceStatusUpdate {
  serviceId: ServiceType
  status: 'active' | 'inactive' | 'error'
  timestamp: string
}

export const useServiceMonitoring = (
  pollingInterval: number = 30000, // 30 seconds
  enableRealTimeUpdates: boolean = true
) => {
  const [state, setState] = useState<ServiceMonitoringState>({
    services: [],
    isLoading: true,
    apiConnected: false,
    lastUpdate: null,
    error: null
  })

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)

  // Status change notifications
  const [statusNotifications, setStatusNotifications] = useState<ServiceStatusUpdate[]>([])

  const updateServiceStatus = useCallback((serviceId: ServiceType, newStatus: 'active' | 'inactive' | 'error') => {
    setState(prevState => ({
      ...prevState,
      services: prevState.services.map(service =>
        service.id === serviceId
          ? { ...service, status: newStatus }
          : service
      ),
      lastUpdate: new Date()
    }))

    // Add notification for status change
    const notification: ServiceStatusUpdate = {
      serviceId,
      status: newStatus,
      timestamp: new Date().toISOString()
    }

    setStatusNotifications(prev => [...prev.slice(-4), notification]) // Keep last 5 notifications
  }, [])

  const loadServices = useCallback(async () => {
    try {
      // Check API health first
      await apiService.healthCheck()
      
      if (!mountedRef.current) return

      setState(prevState => ({ ...prevState, apiConnected: true, error: null }))
      
      // Load services from API
      const response = await apiService.getServices()
      
      if (!mountedRef.current) return

      setState(prevState => ({
        ...prevState,
        services: response.services,
        isLoading: false,
        lastUpdate: new Date()
      }))

      console.log('✅ Services loaded from API:', response.services)
    } catch (error) {
      console.error('❌ Failed to connect to API, using fallback services:', error)
      
      if (!mountedRef.current) return

      // Use the new service configuration system as fallback
      const fallbackServices = getAllServiceConfigs()
      
      setState(prevState => ({
        ...prevState,
        apiConnected: false,
        services: fallbackServices,
        isLoading: false,
        lastUpdate: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      }))
    }
  }, [])

  const checkServiceHealth = useCallback(async () => {
    if (!state.apiConnected || !enableRealTimeUpdates) return

    try {
      const healthResponse = await apiService.healthCheck()
      
      if (!mountedRef.current) return

      // Update service statuses based on health check
      setState(prevState => {
        const updatedServices = prevState.services.map(service => {
          const healthInfo = healthResponse.services[service.id]
          if (healthInfo && healthInfo.status !== service.status) {
            // Notify about status change
            const notification: ServiceStatusUpdate = {
              serviceId: service.id,
              status: healthInfo.status,
              timestamp: new Date().toISOString()
            }
            setStatusNotifications(prev => [...prev.slice(-4), notification])
          }
          
          return healthInfo
            ? { ...service, status: healthInfo.status }
            : service
        })

        return {
          ...prevState,
          services: updatedServices,
          lastUpdate: new Date(),
          error: null
        }
      })
    } catch (error) {
      console.error('Health check failed:', error)
      
      if (!mountedRef.current) return

      // Mark all services as potentially offline if health check fails
      setState(prevState => ({
        ...prevState,
        apiConnected: false,
        error: error instanceof Error ? error.message : 'Health check failed'
      }))
    }
  }, [state.apiConnected, enableRealTimeUpdates])

  const refreshServices = useCallback(async () => {
    setState(prevState => ({ ...prevState, isLoading: true }))
    await loadServices()
  }, [loadServices])

  const clearNotifications = useCallback(() => {
    setStatusNotifications([])
  }, [])

  const dismissNotification = useCallback((index: number) => {
    setStatusNotifications(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Initial load
  useEffect(() => {
    mountedRef.current = true
    loadServices()

    return () => {
      mountedRef.current = false
    }
  }, [loadServices])

  // Set up polling for health checks
  useEffect(() => {
    if (!enableRealTimeUpdates || !state.apiConnected) return

    pollingIntervalRef.current = setInterval(checkServiceHealth, pollingInterval)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [checkServiceHealth, pollingInterval, enableRealTimeUpdates, state.apiConnected])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      mountedRef.current = false
    }
  }, [])

  return {
    ...state,
    statusNotifications,
    updateServiceStatus,
    refreshServices,
    clearNotifications,
    dismissNotification,
    isPolling: enableRealTimeUpdates && state.apiConnected
  }
}